import { isRulerLineType, isWallType, RulerLineType, SingleLineType, WallConnectionEnd, WallConnectionStart, WallType } from "../../types/floorplannerTypes";
import * as THREE from 'three';
import { removeWallConnection } from "./removeWallConnection";
import { floorplannerStore } from '../../store/floorplannerStore';
import { editorStore } from "../../store/editorStore";
import { updateAlignmentLines } from "./updateAligmentLines";
import { renderStore } from "../../store/renderStore";
import { calculateAlignmentSnap } from '../../store/snapToOtherObjects';
import { getArcTangentAtEndpoint } from '../../utils/arcUtils';
import { runInAction } from "mobx";

/**
 * Returns the perpendicular (wall-thickness direction) to use at each endpoint
 * when computing edge-point alignment corners for a whole-wall drag.
 *
 * For arc walls the Bezier tangent at each endpoint defines the wall's visual
 * face direction there. The chord-perpendicular (used for straight walls) does
 * not match the rendered corner for curved walls, which caused alignment lines
 * to snap the wall's centerline/chord instead of its visible edge.
 *
 * Under a pure translation (whole-wall drag), tangent directions are invariant,
 * so we can safely use the wall's original tangents.
 */
const getEndpointPerpendicularsForWholeWallAlignment = (
    object: WallType | SingleLineType | RulerLineType,
    newStart: THREE.Vector2,
    newEnd: THREE.Vector2,
): { startPerp: THREE.Vector2; endPerp: THREE.Vector2 } => {
    const isArcWall = isWallType(object) && object.lineForm === "arc" && !!object.controlPoint;

    let startDir: THREE.Vector2;
    let endDir: THREE.Vector2;
    if (isArcWall) {
        startDir = getArcTangentAtEndpoint(object, "start");
        endDir = getArcTangentAtEndpoint(object, "end");
    } else {
        const chord = new THREE.Vector2(newEnd.x - newStart.x, newEnd.y - newStart.y);
        if (chord.lengthSq() > 1e-12) {
            chord.normalize();
        } else {
            chord.set(1, 0);
        }
        startDir = chord;
        endDir = chord.clone();
    }

    return {
        startPerp: new THREE.Vector2(-startDir.y, startDir.x),
        endPerp: new THREE.Vector2(-endDir.y, endDir.x),
    };
};

type AlignmentConnectionEnd = typeof WallConnectionStart | typeof WallConnectionEnd;

type EdgeAlignmentSnapResult = {
    point: THREE.Vector2;
    snappedStart: THREE.Vector2;
    snappedEnd: THREE.Vector2;
    axes: number;
    distance: number;
    connectionEnd: AlignmentConnectionEnd;
};

/**
 * Drag the whole wall handle, it will move the whole wall releasing any connections
 *
 * WARNING: When dragMode=false (default), this function aggressively clears connections
 * and may mutate state mid-operation. Non-drag consumers should consider:
 * - Using explicit connection management via parameters
 * - Wrapping calls with beginWallDrag/endWallDrag to preserve/restore connections
 * - Using dragMode=true for visual feedback without state changes
 *
 * @param source The object to drag
 * @param startOffsetX The start x offset of the wall
 * @param startOffsetY The start y offset of the wall
 * @param endOffsetX The end x offset of the wall
 * @param endOffsetY The end y offset of the wall
 * @param newX The new x position of the wall
 * @param newY The new y position of the wall
 * @param delta The delta vector
 * @param dragMode When true, uses read-only snapping for visual feedback without affecting other walls
 * @returns boolean If the wall was snapped
 */
export const dragWallHandle = (
    source: WallType | SingleLineType | RulerLineType,
    startOffsetX: number,
    startOffsetY: number,
    endOffsetX: number,
    endOffsetY: number,
    newX: number,
    newY: number,
    delta: THREE.Vector2,
    dragMode: boolean = false,
): boolean => {
    const object = floorplannerStore.findObjectId(source.id);
    if (!object) {
        return false;
    }
    let snapped = false;
    const snapTolerance = editorStore.interactionSnappingTolerance();
      // Calculate movement delta from original start position
    const intendedNewStart = new THREE.Vector2(newX - startOffsetX, newY - startOffsetY);
    const wallDelta = intendedNewStart.clone().sub(object.start);
    const tmpDelta = new THREE.Vector2();

    // Move both ends together maintaining wall length and angle
    const newStart = object.start.clone().add(wallDelta);
    const newEnd = object.end.clone().add(wallDelta);

    // Calculate new control point if exists (but don't update yet)
    let newControlPoint: THREE.Vector2 | null = null;
    if (object.controlPoint) {
        newControlPoint = new THREE.Vector2(
            object.controlPoint.x + wallDelta.x,
            object.controlPoint.y + wallDelta.y
        );
    }

    delta.set(wallDelta.x, wallDelta.y);

    let startSnapped = false, endSnapped = false;
    const getWholeWallPreviewOverrides = (start: THREE.Vector2, end: THREE.Vector2) => {
        const overrides: { start: THREE.Vector2; end: THREE.Vector2; controlPoint?: THREE.Vector2 } = {
            start: start.clone(),
            end: end.clone(),
        };

        if (object.controlPoint) {
            const translation = start.clone().sub(object.start);
            overrides.controlPoint = object.controlPoint.clone().add(translation);
        }

        return overrides;
    };

    // SNAPPING PRIORITY ORDER for whole wall drag:
    // 1. Wall edge corners (for wall-to-wall connections) - HIGHEST PRIORITY
    // 2. Alignment lines - LOWEST PRIORITY
    // Note: No angle snapping for whole wall drag since wall angle is fixed

    // Perform snapping with drag mode support
    if (!dragMode) {
        // Non-drag mode: use snapping logic that updates store state
        // Do not clear connections, only record connections without moving wall
        // SHIFT key disables ALL snapping
        if (!editorStore.isShiftPressed) {
            // Edge snapping is the default mode unless ALT/OPTION key is pressed
            const shouldUseEdgeSnapping = !editorStore.isAltPressed;
            if (shouldUseEdgeSnapping) {
                // Edge mode: use edge-based snapping for start/end commits (default)
                // Edge mode preserves wall length and only records edge connections on commit
                // Avoids center-based snap for midpoints during whole-wall moves - only uses edge-based commit
                // This prevents inadvertently adding mid-point center connections
                const startSnapResult = floorplannerStore.snapToOtherObjectsEdgeBased(object.id, WallConnectionStart, newStart, true, { allowDuringDeferred: false });
                startSnapped = startSnapResult && typeof startSnapResult === 'object' && startSnapResult.snapped;

                const endSnapResult = floorplannerStore.snapToOtherObjectsEdgeBased(object.id, WallConnectionEnd, newEnd, true, { allowDuringDeferred: false });
                endSnapped = endSnapResult && typeof endSnapResult === 'object' && endSnapResult.snapped;
                // Note: No center-based fallback - only edge-based connections are used in edge mode
            } else {
                // Center mode: use center-based snapping (when ALT is pressed)
                const startSnapResult = floorplannerStore.snapToOtherObjects(object.id, WallConnectionStart, newStart, true, { allowDuringDeferred: false });
                startSnapped = startSnapResult && typeof startSnapResult === 'object' && startSnapResult.snapped;

                const endSnapResult = floorplannerStore.snapToOtherObjects(object.id, WallConnectionEnd, newEnd, true, { allowDuringDeferred: false });
                endSnapped = endSnapResult && typeof endSnapResult === 'object' && endSnapResult.snapped;
            }
        }

        // Maintain wall length and don't overwrite positions from snap results
        // The connections are recorded but the wall position is not changed by snapping

        // Try alignment snapping (always check, can override object snapping)
        if (!editorStore.isShiftPressed) {
            // Check if we should use edge points or center points for alignment
            const shouldUseEdgePoints = !editorStore.isAltPressed && object.type === 'wall';

            if (shouldUseEdgePoints) {
                // Edge mode: Check all 4 edge points for alignment.
                // For arc walls the perpendicular at each endpoint is derived from
                // the Bezier tangent there (not the chord) so corners match the
                // wall's rendered outline - see getEndpointPerpendicularsForWholeWallAlignment.
                const { startPerp, endPerp } = getEndpointPerpendicularsForWholeWallAlignment(object, newStart, newEnd);
                const width = (object as WallType).wallWidth || floorplannerStore.wallWidth;
                const halfWidth = width / 2;

                // Calculate all 4 edge points
                const startLeft = new THREE.Vector2(
                    newStart.x + startPerp.x * halfWidth,
                    newStart.y + startPerp.y * halfWidth
                );
                const startRight = new THREE.Vector2(
                    newStart.x - startPerp.x * halfWidth,
                    newStart.y - startPerp.y * halfWidth
                );
                const endLeft = new THREE.Vector2(
                    newEnd.x + endPerp.x * halfWidth,
                    newEnd.y + endPerp.y * halfWidth
                );
                const endRight = new THREE.Vector2(
                    newEnd.x - endPerp.x * halfWidth,
                    newEnd.y - endPerp.y * halfWidth
                );

                // Check all 4 edge points against alignment lines
                // Use WallConnectionStart for start edges, WallConnectionEnd for end edges
                const edgePoints: Array<{ point: THREE.Vector2; label: string; connectionEnd: AlignmentConnectionEnd }> = [
                    { point: startLeft, label: 'startLeft', connectionEnd: WallConnectionStart },
                    { point: startRight, label: 'startRight', connectionEnd: WallConnectionStart },
                    { point: endLeft, label: 'endLeft', connectionEnd: WallConnectionEnd },
                    { point: endRight, label: 'endRight', connectionEnd: WallConnectionEnd }
                ];

                let bestSnap: EdgeAlignmentSnapResult | null = null;

                for (const edge of edgePoints) {
                    const overrides = { start: newStart, end: newEnd };
                    const preview = floorplannerStore.previewAlignmentSnap(object.id, edge.connectionEnd, edge.point, overrides);
                    const axes = (preview.snappedX ? 1 : 0) + (preview.snappedY ? 1 : 0);

                    if (preview.snapped && axes > 0) {
                        // Calculate how much we need to move the wall to snap this edge point to the alignment line
                        // Use targetX/targetY (alignment line positions) not preview.start (wall position)
                        const snapDelta = new THREE.Vector2(
                            (preview.snappedX && preview.targetX !== undefined ? (preview.targetX - edge.point.x) : 0),
                            (preview.snappedY && preview.targetY !== undefined ? (preview.targetY - edge.point.y) : 0)
                        );
                        const distance = snapDelta.length();

                        // Apply delta to get snapped positions
                        const snappedStart = newStart.clone().add(snapDelta);
                        const snappedEnd = newEnd.clone().add(snapDelta);

                        if (!bestSnap || axes > bestSnap.axes || (axes === bestSnap.axes && distance < bestSnap.distance)) {
                            bestSnap = {
                                point: edge.point,
                                snappedStart,
                                snappedEnd,
                                axes,
                                distance,
                                connectionEnd: edge.connectionEnd,
                            };
                        }
                    }
                }

                if (bestSnap) {
                    newStart.copy(bestSnap.snappedStart);
                    newEnd.copy(bestSnap.snappedEnd);
                    const alignmentKey = bestSnap.connectionEnd === WallConnectionStart ? "start" : "end";
                    const targetPoint = alignmentKey === "start" ? newStart : newEnd;
                    if (alignmentKey === "start") {
                        startSnapped = true;
                    } else {
                        endSnapped = true;
                    }
                    updateAlignmentLines(object.id, targetPoint.x, targetPoint.y, alignmentKey);
                }
            } else {
                // Center mode: Use original center point logic (when ALT is pressed or non-walls)
                const overrides = { start: newStart, end: newEnd };
                const startPreview = floorplannerStore.previewAlignmentSnap(object.id, WallConnectionStart, newStart, overrides);
                const endPreview = floorplannerStore.previewAlignmentSnap(object.id, WallConnectionEnd, newEnd, overrides);

                const startAxes = (startPreview.snappedX ? 1 : 0) + (startPreview.snappedY ? 1 : 0);
                const endAxes = (endPreview.snappedX ? 1 : 0) + (endPreview.snappedY ? 1 : 0);

                let chosenEnd: number | null = null;
                let chosenPreview: typeof startPreview | null = null;

                if (startAxes > endAxes) {
                    chosenEnd = WallConnectionStart;
                    chosenPreview = startPreview;
                } else if (endAxes > startAxes) {
                    chosenEnd = WallConnectionEnd;
                    chosenPreview = endPreview;
                } else {
                    const startDelta = startPreview.snapped ? newStart.distanceTo(startPreview.start) : Infinity;
                    const endDelta = endPreview.snapped ? newEnd.distanceTo(endPreview.end) : Infinity;

                    if (startDelta <= endDelta) {
                        chosenEnd = WallConnectionStart;
                        chosenPreview = startPreview;
                    } else {
                        chosenEnd = WallConnectionEnd;
                        chosenPreview = endPreview;
                    }
                }

                if (chosenPreview && chosenPreview.snapped && chosenEnd !== null) {
                    newStart.copy(chosenPreview.start);
                    newEnd.copy(chosenPreview.end);

                    if (chosenEnd === WallConnectionStart) {
                        startSnapped = true;
                        updateAlignmentLines(object.id, newStart.x, newStart.y, "start");
                    } else {
                        endSnapped = true;
                        updateAlignmentLines(object.id, newEnd.x, newEnd.y, "end");
                    }
                }
            }
        }
    } else {
        // Drag mode: use read-only snapping for visual feedback without affecting other walls
        let startSnapCandidate: ReturnType<typeof floorplannerStore.getEdgeSnappingPositionForDrag> = null;
        let endSnapCandidate: ReturnType<typeof floorplannerStore.getEdgeSnappingPositionForDrag> = null;
        let startSnapPoint: THREE.Vector2 | null = null;
        let endSnapPoint: THREE.Vector2 | null = null;

        let startEdgeSnapApplied = false;
        let endEdgeSnapApplied = false;

        // Edge snapping is the default mode unless ALT/OPTION key is pressed
        const shouldUseEdgeSnapping = !editorStore.isAltPressed;

        // SHIFT key disables ALL snapping during drag preview
        if (!editorStore.isShiftPressed) {
            if (shouldUseEdgeSnapping) {
                // Edge mode: use edge-based snapping for previews (default)
                const previewOverrides = getWholeWallPreviewOverrides(newStart, newEnd);
                startSnapCandidate = floorplannerStore.getEdgeSnappingPositionForDrag(object.id, "start", newStart, previewOverrides);
                endSnapCandidate = floorplannerStore.getEdgeSnappingPositionForDrag(object.id, "end", newEnd, previewOverrides);
                startSnapPoint = startSnapCandidate?.snappingPosition || null;
                endSnapPoint = endSnapCandidate?.snappingPosition || null;
            } else {
                // Center mode: use center-based snapping (when ALT/OPTION is pressed)
                startSnapPoint = floorplannerStore.getSnappingPositionForDrag(object.id, WallConnectionStart, newStart);
                endSnapPoint = floorplannerStore.getSnappingPositionForDrag(object.id, WallConnectionEnd, newEnd);
            }

            // Calculate distances for choosing best snap
            const startDist = startSnapPoint ? newStart.distanceTo(startSnapPoint) : Infinity;
            const endDist = endSnapPoint ? newEnd.distanceTo(endSnapPoint) : Infinity;
            const edgeSnapApplyThreshold = snapTolerance * 2;

            if (startSnapPoint && startDist <= edgeSnapApplyThreshold && startDist <= endDist) {
                // Snap to start point - move entire wall
                tmpDelta.copy(startSnapPoint).sub(newStart);
                newStart.add(tmpDelta);
                newEnd.add(tmpDelta);
                startSnapped = true;
                startEdgeSnapApplied = shouldUseEdgeSnapping;
            } else if (endSnapPoint && endDist <= edgeSnapApplyThreshold) {
                // Snap to end point - move entire wall
                tmpDelta.copy(endSnapPoint).sub(newEnd);
                newStart.add(tmpDelta);
                newEnd.add(tmpDelta);
                endSnapped = true;
                endEdgeSnapApplied = shouldUseEdgeSnapping;
            }
        }

        if (!editorStore.isShiftPressed) {
            // Always check alignment lines (even if we already snapped to another wall)
            const shouldUseEdgePoints = !editorStore.isAltPressed && object.type === 'wall';

            if (shouldUseEdgePoints) {
                // Edge mode: Check all 4 edge points for alignment.
                // Use tangent-based perpendiculars at each endpoint for arc walls so
                // the evaluated corners match what the user sees - otherwise the
                // chord-perpendicular collapses curved-wall corners onto the chord
                // and makes alignment look like center-based snapping.
                const { startPerp, endPerp } = getEndpointPerpendicularsForWholeWallAlignment(object, newStart, newEnd);
                const width = (object as WallType).wallWidth || floorplannerStore.wallWidth;
                const halfWidth = width / 2;

                // Calculate all 4 edge points
                const startLeft = new THREE.Vector2(
                    newStart.x + startPerp.x * halfWidth,
                    newStart.y + startPerp.y * halfWidth
                );
                const startRight = new THREE.Vector2(
                    newStart.x - startPerp.x * halfWidth,
                    newStart.y - startPerp.y * halfWidth
                );
                const endLeft = new THREE.Vector2(
                    newEnd.x + endPerp.x * halfWidth,
                    newEnd.y + endPerp.y * halfWidth
                );
                const endRight = new THREE.Vector2(
                    newEnd.x - endPerp.x * halfWidth,
                    newEnd.y - endPerp.y * halfWidth
                );

                // Check all 4 edge points against alignment lines
                // Use WallConnectionStart for start edges, WallConnectionEnd for end edges
                const edgePoints: Array<{ point: THREE.Vector2; label: string; connectionEnd: AlignmentConnectionEnd }> = [
                    { point: startLeft, label: 'startLeft', connectionEnd: WallConnectionStart },
                    { point: startRight, label: 'startRight', connectionEnd: WallConnectionStart },
                    { point: endLeft, label: 'endLeft', connectionEnd: WallConnectionEnd },
                    { point: endRight, label: 'endRight', connectionEnd: WallConnectionEnd }
                ];

                const overrides = { start: newStart, end: newEnd };
                const dragState = floorplannerStore.wallDragState.get(object.id);

                let bestSnap: EdgeAlignmentSnapResult | null = null;

                for (const edge of edgePoints) {
                    const alignmentKey = edge.connectionEnd === WallConnectionStart ? "start" : "end";
                    const preview = calculateAlignmentSnap(object, edge.connectionEnd, edge.point, {
                        overrides,
                        previous: dragState?.alignmentSnap?.[alignmentKey],
                    });
                    const axes = (preview.snappedX ? 1 : 0) + (preview.snappedY ? 1 : 0);

                    if (preview.snapped && axes > 0) {
                        // Calculate how much we need to move the wall to snap this edge point to the alignment line
                        // Use targetX/targetY (alignment line positions) not preview.start (wall position)
                        const snapDelta = new THREE.Vector2(
                            (preview.snappedX && preview.targetX !== undefined ? (preview.targetX - edge.point.x) : 0),
                            (preview.snappedY && preview.targetY !== undefined ? (preview.targetY - edge.point.y) : 0)
                        );
                        const distance = snapDelta.length();

                        // Apply delta to get snapped positions
                        const snappedStart = newStart.clone().add(snapDelta);
                        const snappedEnd = newEnd.clone().add(snapDelta);

                        if (!bestSnap || axes > bestSnap.axes || (axes === bestSnap.axes && distance < bestSnap.distance)) {
                            bestSnap = {
                                point: edge.point,
                                snappedStart,
                                snappedEnd,
                                axes,
                                distance,
                                connectionEnd: edge.connectionEnd,
                            };
                        }
                    }
                }

                if (bestSnap) {
                    tmpDelta.copy(bestSnap.snappedStart).sub(newStart);
                    newStart.copy(bestSnap.snappedStart);
                    newEnd.copy(bestSnap.snappedEnd);
                    const alignmentKey = bestSnap.connectionEnd === WallConnectionStart ? "start" : "end";
                    if (alignmentKey === "start") {
                        startSnapped = true;
                    } else {
                        endSnapped = true;
                    }

                    // Update drag state for next frame
                    if (dragState) {
                        if (!dragState.alignmentSnap) {
                            dragState.alignmentSnap = {};
                        }
                        // Store state for hysteresis on next frame
                        const targetPoint = alignmentKey === "start" ? bestSnap.snappedStart : bestSnap.snappedEnd;
                        dragState.alignmentSnap[alignmentKey] = {
                            snappedX: true,
                            snappedY: true,
                            targetX: targetPoint.x,
                            targetY: targetPoint.y,
                        };
                    }
                }
            } else {
                // Center mode: Use original center point logic (when ALT is pressed or non-walls)
                const overrides = { start: newStart, end: newEnd };

                // Get previous state for hysteresis
                const dragState = floorplannerStore.wallDragState.get(object.id);
                const startPrevious = dragState?.alignmentSnap?.start;
                const endPrevious = dragState?.alignmentSnap?.end;

                // Use calculateAlignmentSnap directly to avoid triggering renders
                const startPreview = calculateAlignmentSnap(object, WallConnectionStart, newStart, {
                    overrides,
                    previous: startPrevious,
                });
                const endPreview = calculateAlignmentSnap(object, WallConnectionEnd, newEnd, {
                    overrides,
                    previous: endPrevious,
                });

                const startAxes = (startPreview.snappedX ? 1 : 0) + (startPreview.snappedY ? 1 : 0);
                const endAxes = (endPreview.snappedX ? 1 : 0) + (endPreview.snappedY ? 1 : 0);

                let chosenEnd: number | null = null;
                let chosenPreview: typeof startPreview | null = null;

                if (startAxes > endAxes) {
                    chosenEnd = WallConnectionStart;
                    chosenPreview = startPreview;
                } else if (endAxes > startAxes) {
                    chosenEnd = WallConnectionEnd;
                    chosenPreview = endPreview;
                } else {
                    const startDelta = startPreview.snapped ? newStart.distanceTo(startPreview.start) : Infinity;
                    const endDelta = endPreview.snapped ? newEnd.distanceTo(endPreview.end) : Infinity;

                    if (startDelta <= endDelta) {
                        chosenEnd = WallConnectionStart;
                        chosenPreview = startPreview;
                    } else {
                        chosenEnd = WallConnectionEnd;
                        chosenPreview = endPreview;
                    }
                }

                if (chosenPreview && chosenPreview.snapped && chosenEnd !== null) {
                    tmpDelta.copy(chosenPreview.start).sub(newStart);
                    newStart.copy(chosenPreview.start);
                    newEnd.copy(chosenPreview.end);

                    // Update drag state for next frame (will be batched with position update below)
                    if (dragState) {
                        if (!dragState.alignmentSnap) {
                            dragState.alignmentSnap = {};
                        }
                        dragState.alignmentSnap.start = {
                            snappedX: startPreview.snappedX,
                            snappedY: startPreview.snappedY,
                            targetX: startPreview.targetX,
                            targetY: startPreview.targetY,
                        };
                        dragState.alignmentSnap.end = {
                            snappedX: endPreview.snappedX,
                            snappedY: endPreview.snappedY,
                            targetX: endPreview.targetX,
                            targetY: endPreview.targetY,
                        };
                    }

                    if (chosenEnd === WallConnectionStart) {
                        startSnapped = true;
                    } else {
                        endSnapped = true;
                    }
                }
            }
        }

        if (shouldUseEdgeSnapping) {
            if (startEdgeSnapApplied) {
                const refreshedOverrides = getWholeWallPreviewOverrides(newStart, newEnd);
                const refreshedStart = floorplannerStore.getEdgeSnappingPositionForDrag(object.id, "start", newStart, refreshedOverrides);
                if (refreshedStart) {
                    startSnapCandidate = refreshedStart;
                }
            }
            if (endEdgeSnapApplied) {
                const refreshedOverrides = getWholeWallPreviewOverrides(newStart, newEnd);
                const refreshedEnd = floorplannerStore.getEdgeSnappingPositionForDrag(object.id, "end", newEnd, refreshedOverrides);
                if (refreshedEnd) {
                    endSnapCandidate = refreshedEnd;
                }
            }
        }

        if (shouldUseEdgeSnapping) {
            floorplannerStore.storeEdgeSnappingPreview(
                object.id,
                "start",
                startEdgeSnapApplied && startSnapCandidate ? startSnapCandidate : null
            );
            floorplannerStore.storeEdgeSnappingPreview(
                object.id,
                "end",
                endEdgeSnapApplied && endSnapCandidate ? endSnapCandidate : null
            );
        } else {
            floorplannerStore.storeEdgeSnappingPreview(object.id, "start", null);
            floorplannerStore.storeEdgeSnappingPreview(object.id, "end", null);
        }
    }

    // Update position and control point atomically to prevent multiple renders
    runInAction(() => {
        // Recalculate control point delta from actual final positions
        // (snapping may have adjusted newStart/newEnd after the initial wallDelta calculation)
        if (object.controlPoint) {
            const actualDelta = newStart.clone().sub(object.start);
            newControlPoint = new THREE.Vector2(
                object.controlPoint.x + actualDelta.x,
                object.controlPoint.y + actualDelta.y
            );
        }

        floorplannerStore.updateObjectPosition(object.id, newStart, newEnd, {
            bypassEdgeConstraints: true,
            skipInvalidation: dragMode,
        });
        if (newControlPoint) {
            floorplannerStore.setObjectProperty(object.id, 'controlPoint', newControlPoint);
        }
        
        // Synchronously update geometry for the dragged wall so the mesh follows the mouse immediately
        // Without this, the mesh only updates when lastUpdate changes asynchronously, causing lag
        if (isWallType(object)) {
            floorplannerStore.setObjectProperty(object.id, "cornerState", undefined);
            renderStore.composeWallShape(object.id, undefined, true);
            renderStore.trimShapes(object as WallType);
            if (renderStore.isVisualUpdateAllowed(object.id)) {
                renderStore.generateOutlineSegments(object.id);
            }
        }
        
        // Force increment geometry version to invalidate cache
        floorplannerStore.incrementWallGeometryVersion(object.id);
    });

    return startSnapped || endSnapped;
}
